export interface PickupSlotOption {
  readonly startAt: string;
  readonly endAt: string;
  /** When false, chip renders muted/disabled (MOB-03). Default true when omitted. */
  readonly available?: boolean;
}

export interface PickupDayGroup {
  readonly dayKey: string;
  readonly slots: readonly PickupSlotOption[];
}

export interface PickupDayChipLabels {
  readonly today: string;
  readonly tomorrow: string;
}

export interface PickupDayChipDisplay {
  readonly primary: string;
  readonly secondary: string;
}

/** Stable YYYY-MM-DD key in the user's local calendar. */
export function getLocalDayKey(isoOrDate: string | Date): string {
  const date = typeof isoOrDate === 'string' ? new Date(isoOrDate) : isoOrDate;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function groupPickupSlotsByDay(slots: readonly PickupSlotOption[]): PickupDayGroup[] {
  const map = new Map<string, PickupSlotOption[]>();

  for (const slot of slots) {
    const dayKey = getLocalDayKey(slot.startAt);
    const existing = map.get(dayKey);
    if (existing === undefined) {
      map.set(dayKey, [slot]);
    } else {
      existing.push(slot);
    }
  }

  return Array.from(map.entries())
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([dayKey, daySlots]) => ({
      dayKey,
      slots: [...daySlots].sort((left, right) => left.startAt.localeCompare(right.startAt)),
    }));
}

export function formatPickupSlotTime(iso: string, localeTag: string): string {
  return new Intl.DateTimeFormat(localeTag, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

export function formatPickupSlotDateTime(iso: string, localeTag: string): string {
  return new Intl.DateTimeFormat(localeTag, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

function parseLocalDayKey(dayKey: string): Date {
  const [yearText, monthText, dayText] = dayKey.split('-');
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  return new Date(year, month - 1, day, 12, 0, 0, 0);
}

export function formatPickupDayChip(
  dayKey: string,
  localeTag: string,
  labels: PickupDayChipLabels,
): PickupDayChipDisplay {
  const date = parseLocalDayKey(dayKey);
  const todayKey = getLocalDayKey(new Date());
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowKey = getLocalDayKey(tomorrow);

  const weekday = new Intl.DateTimeFormat(localeTag, { weekday: 'short' }).format(date);
  const numericDate = new Intl.DateTimeFormat(localeTag, {
    day: 'numeric',
    month: 'numeric',
  }).format(date);

  if (dayKey === todayKey) {
    return { primary: labels.today, secondary: numericDate };
  }

  if (dayKey === tomorrowKey) {
    return { primary: labels.tomorrow, secondary: numericDate };
  }

  return { primary: weekday, secondary: numericDate };
}

export function resolveInitialPickupDayKey(
  dayGroups: readonly PickupDayGroup[],
  selectedStartAt: string | null,
): string | null {
  if (dayGroups.length === 0) {
    return null;
  }

  if (selectedStartAt !== null) {
    const selectedDayKey = getLocalDayKey(selectedStartAt);
    const hasSelectedDay = dayGroups.some((group) => group.dayKey === selectedDayKey);
    if (hasSelectedDay) {
      return selectedDayKey;
    }
  }

  return dayGroups[0]?.dayKey ?? null;
}
